import { useMemo, type VoidFunctionComponent } from 'react';

import { BinaryFetcherName } from '../../../../../shared/fetchers';
import Locator from '../../../../../shared/Locator';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { useParams } from '../../../../contexts/RouterContext';
import { useIsSecurityEnabled } from '../../../../helpers/features/security';
import useReleaseGroupAllProjects from '../../../../hooks/api/releaseGroups/useReleaseGroupAllProjects';
import useHasPermission from '../../../../shared/permissions/useHasPermission';
import Subnav from '../../../generic/Subnav';
import { useCanDisplayExecutiveVulnerabilityReport } from '../../../reportsv3/hooks/useCanDisplayExecutiveVulnerabilityReport';
import { useCanDisplaySecurityOptions } from '../../generic/hooks';
import { ReportCategories, ReportCategoryTitles } from '../../generic/ReportGenerationV2/utils';
import type { ReportCategory } from '../../generic/shared/types';

/**
 * The Reports V3 routes use camelCase slugs (eg. "remediationGuidance") while the old
 * ReportCategories use kebab-case (eg. "remediation-guidance"). This map bridges
 * the mismatch for any category whose slug differs between the two systems.
 *
 * TODO: Update ReportCategories to use the camelCase values directly and remove this map.
 */
const reportsV3SlugOverrides: Partial<Record<ReportCategory, string>> = {
  [ReportCategories.RemediationGuidance]: 'remediationGuidance',
};

/**
 * Mini hook which just memoizes the enabled report types based on the
 * security and PDF reports feature flag and and the projects fetcher types.
 */
const useEnabledReportTypes = () => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isSecurityEnabled = useIsSecurityEnabled();
  const params = useParams<{ projectGroupId: number }>();
  const allProjectLocators = useReleaseGroupAllProjects(params.projectGroupId).data ?? [];
  const areAllProjectsBinary = allProjectLocators.every(loc => {
    const { fetcher } = Locator.parse(loc);
    return fetcher === BinaryFetcherName;
  });

  return useMemo(() => {
    const excludedReports: string[] = [];
    if (!isSecurityAndPDFReportsEnabled) {
      excludedReports.push(ReportCategories.Vulnerability);
    }
    if (!isSecurityEnabled || areAllProjectsBinary) {
      excludedReports.push(ReportCategories.RemediationGuidance);
    }

    return Object.entries(ReportCategoryTitles).filter(([key]) => !excludedReports.includes(key));
  }, [isSecurityAndPDFReportsEnabled, isSecurityEnabled, areAllProjectsBinary]);
};

type Props = {
  baseUrl: string;
  releaseId?: number;
};

/**
 * ReleaseGroupSubnavItemReports is the Subnav item for the reports section of a release group.
 */
const ReleaseGroupSubnavItemReports: VoidFunctionComponent<Props> = ({
  baseUrl,
  releaseId,
}) => {
  const enabledReportTypes = useEnabledReportTypes();
  const canDisplayExecutiveVulnerabilityReport = useCanDisplayExecutiveVulnerabilityReport();

  const hasPermission = useHasPermission();
  const canGenerateReport = hasPermission({ resourceType: ResourceType.ReleaseGroupReportAny, action: Action.Create })
    || (releaseId && hasPermission({
      resourceType: ResourceType.ReleaseGroupReport,
      action: Action.Create,
      releaseGroupId: releaseId,
    }));

  if (!canGenerateReport) {
    return null;
  }

  return (
    <Subnav.Dropdown
      to={`${baseUrl}/reports`}
      navKey="reports"
    >
      <Subnav.Dropdown.RootItem>
        Reports
      </Subnav.Dropdown.RootItem>
      {enabledReportTypes.map(([reportType, title]) => {
        const slug = reportsV3SlugOverrides[reportType as ReportCategory] ?? reportType;

        return (
          <Subnav.Dropdown.Item
            key={slug}
            to={`${baseUrl}/reports/${slug}`}
            navKey={`reports/${slug}`}
          >
            {title}
          </Subnav.Dropdown.Item>
        );
      })}
      {canDisplayExecutiveVulnerabilityReport && (
        <Subnav.Dropdown.Item
          to={`${baseUrl}/reports/executiveVulnerability`}
          navKey="reports/executiveVulnerability"
        >
          Executive Vulnerability
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ReleaseGroupSubnavItemReports;
